/* LFX-02 — entry module
 * Lenis smooth scroll · GSAP ScrollTrigger choreography · SplitType reveals · magnetic CTAs.
 */
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';
import SplitType from 'split-type';

gsap.registerPlugin(ScrollTrigger);

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- Lenis smooth scroll ---------- */
function initLenis() {
  if (reduceMotion) return null;
  const lenis = new Lenis({
    duration: 1.15,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothWheel: true,
  });
  lenis.on('scroll', ScrollTrigger.update);
  gsap.ticker.add((time) => lenis.raf(time * 1000));
  gsap.ticker.lagSmoothing(0);
  return lenis;
}

/* ---------- SplitType char reveals on .reveal-chars ---------- */
function splitTitles() {
  document.querySelectorAll('.reveal-chars').forEach((el) => {
    new SplitType(el, { types: 'chars', tagName: 'span' });
  });
}

function staggerCharsIn(el, delay = 0) {
  const chars = el.querySelectorAll('.char');
  chars.forEach((c, i) => {
    setTimeout(() => c.classList.add('in'), delay + i * 18);
  });
}

/* ---------- Magnetic CTAs ---------- */
function initMagnetic() {
  if (reduceMotion) return;
  document.querySelectorAll('[data-magnetic]').forEach((btn) => {
    const strength = 0.35;
    const setX = gsap.quickTo(btn, 'x', { duration: 0.5, ease: 'power3.out' });
    const setY = gsap.quickTo(btn, 'y', { duration: 0.5, ease: 'power3.out' });
    btn.addEventListener('mousemove', (e) => {
      const r = btn.getBoundingClientRect();
      const dx = e.clientX - (r.left + r.width / 2);
      const dy = e.clientY - (r.top + r.height / 2);
      setX(dx * strength);
      setY(dy * strength);
    });
    btn.addEventListener('mouseleave', () => { setX(0); setY(0); });
  });
}

/* ---------- Reveal-on-scroll for [data-fx=fade-up] ---------- */
function initFadeUps() {
  document.querySelectorAll('[data-fx="fade-up"]').forEach((el) => {
    gsap.fromTo(
      el,
      { y: 24, opacity: 0 },
      {
        y: 0, opacity: 1, duration: 1.1, ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 88%', once: true },
      },
    );
  });
}

/* ---------- Stage rows: scrub bar + content fade-in ---------- */
function initStages() {
  document.querySelectorAll('.stage').forEach((el) => {
    ScrollTrigger.create({
      trigger: el,
      start: 'top 80%',
      onEnter: () => el.classList.add('in'),
      once: true,
    });
  });
}

/* ---------- Embodiment cards: stagger fade-in ---------- */
function initMachineReveals() {
  document.querySelectorAll('.machine').forEach((el) => {
    ScrollTrigger.create({
      trigger: el,
      start: 'top 85%',
      onEnter: () => el.classList.add('in'),
      once: true,
    });
  });
}

/* ---------- Statement: word-by-word brighten on scrub ---------- */
function initStatementScrub() {
  const target = document.querySelector('[data-scrub-words]');
  if (!target) return;
  // Wrap each word in a span (preserving the existing .dim span semantics)
  target.querySelectorAll('span, :scope').forEach(() => {}); // no-op
  // Manually wrap text nodes inside target (top-level + within .dim) in .word spans
  function wrap(node) {
    const next = [];
    node.childNodes.forEach((n) => {
      if (n.nodeType === Node.TEXT_NODE) {
        const frag = document.createDocumentFragment();
        const parts = n.textContent.split(/(\s+)/);
        parts.forEach((p) => {
          if (/^\s+$/.test(p)) frag.appendChild(document.createTextNode(p));
          else if (p.length) {
            const s = document.createElement('span');
            s.className = 'word';
            s.textContent = p;
            // Preserve dim color via inherit from parent
            if (n.parentElement.classList.contains('dim')) {
              s.dataset.dim = '1';
            }
            frag.appendChild(s);
          }
        });
        next.push({ node: n, replacement: frag });
      } else if (n.nodeType === Node.ELEMENT_NODE) {
        wrap(n);
      }
    });
    next.forEach(({ node, replacement }) => node.parentNode.replaceChild(replacement, node));
  }
  wrap(target);

  const words = target.querySelectorAll('.word');
  ScrollTrigger.create({
    trigger: target,
    start: 'top 80%',
    end: 'bottom 30%',
    scrub: true,
    onUpdate: (self) => {
      const p = self.progress;
      const n = words.length;
      words.forEach((w, i) => {
        const wp = Math.max(0, Math.min(1, p * n - i));
        const dim = w.dataset.dim === '1';
        const base = dim ? 90 : 255;
        const target = dim ? 200 : 255;
        const min = dim ? 45 : 60;
        const v = Math.round(min + (target - min) * wp);
        const a = 0.18 + 0.82 * wp;
        const tint = dim ? v : 255;
        w.style.color = `rgba(${tint}, ${tint}, ${tint}, ${a})`;
      });
    },
  });
}

/* ---------- Faculty list — hover/click highlights the matching row ---------- */
function initFacultyList() {
  const els = document.querySelectorAll('.arch-faculty');
  els.forEach((el) => {
    el.addEventListener('mouseenter', () => setActive(+el.dataset.i));
    el.addEventListener('click', () => setActive(+el.dataset.i));
  });
  function setActive(i) {
    els.forEach((el) => el.classList.toggle('active', +el.dataset.i === i));
  }
}

/* ---------- Hero video mouse parallax (the WebM robot) ---------- */
function initHeroVideoParallax() {
  if (reduceMotion) return;
  const video = document.querySelector('.hero-video');
  if (!video) return;
  let tx = 0, ty = 0, cx = 0, cy = 0;
  window.addEventListener('mousemove', (e) => {
    tx = (e.clientX / window.innerWidth - 0.5) * 10;
    ty = (e.clientY / window.innerHeight - 0.5) * 6;
  });
  function tick() {
    cx += (tx - cx) * 0.05;
    cy += (ty - cy) * 0.05;
    video.style.translate = `${cx.toFixed(2)}px ${cy.toFixed(2)}px`;
    requestAnimationFrame(tick);
  }
  tick();
}

/* ---------- Request access modal ---------- */
function initAccessModal() {
  const modal = document.getElementById('accessModal');
  const form = document.getElementById('accessForm');
  if (!modal || !form) return;

  const status = form.querySelector('.access-status');
  const submitBtn = form.querySelector('.access-submit');
  const submitLabel = form.querySelector('.access-submit-label');
  let lastFocus = null;

  function open() {
    lastFocus = document.activeElement;
    modal.classList.add('open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    const first = form.querySelector('input, select, textarea');
    if (first) first.focus();
  }
  function close() {
    modal.classList.remove('open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    if (lastFocus) lastFocus.focus();
  }

  document.querySelectorAll('[data-open-access]').forEach((b) => b.addEventListener('click', open));
  modal.querySelectorAll('[data-close-access]').forEach((b) => b.addEventListener('click', close));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('open')) close();
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    status.textContent = '';
    status.classList.remove('is-error', 'is-ok');

    const data = Object.fromEntries(new FormData(form).entries());
    if (data.website) return; // honeypot tripped — silently drop
    if (!data.name || !data.email || !data.company_info) {
      status.textContent = 'Please fill the required fields.';
      status.classList.add('is-error');
      return;
    }

    submitBtn.disabled = true;
    submitLabel.textContent = 'Submitting…';
    try {
      const res = await fetch('/api/access', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      form.classList.add('is-success');
      status.textContent = 'Request received. We will reach out shortly.';
      status.classList.add('is-ok');
      submitBtn.style.display = 'none';
    } catch (err) {
      status.textContent = 'Could not submit. Email yt@auletechnologies.com instead.';
      status.classList.add('is-error');
      submitBtn.disabled = false;
      submitLabel.textContent = 'Submit request';
    }
  });
}

/* ---------- Boot ---------- */
async function boot() {
  splitTitles();
  initLenis();
  initFadeUps();
  initStages();
  initMachineReveals();
  initStatementScrub();
  initMagnetic();
  initHeroVideoParallax();

  // Hero entrance: fire after a tick so SplitType chars exist in DOM
  requestAnimationFrame(() => {
    const heroH1 = document.querySelector('.hero-statement .reveal-chars');
    if (heroH1) staggerCharsIn(heroH1, 250);
  });

  // Trigger char reveals on other titles when they enter view
  document.querySelectorAll('.reveal-chars').forEach((el) => {
    if (el.closest('.hero-statement')) return; // hero already triggered above
    ScrollTrigger.create({
      trigger: el,
      start: 'top 85%',
      once: true,
      onEnter: () => staggerCharsIn(el, 0),
    });
  });

  initFacultyList();
  initAccessModal();

  // ScrollTrigger needs a refresh after async content settles + on font load
  ScrollTrigger.refresh();
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => ScrollTrigger.refresh());
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
